import { hmac } from "@noble/hashes/hmac.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { fetchAuthSession } from "aws-amplify/auth";
import type { AwsIotCoreConfig } from "./types";

/** SHA-256 hash of an empty string, used as the payload hash for unsigned requests */
const EMPTY_PAYLOAD_SHA256 =
	"e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";

// Pre-computed hex lookup table
const HEX_CHARS = /* @__PURE__ */ Array.from({ length: 256 }, (_, i) =>
	i.toString(16).padStart(2, "0"),
);

// Shared TextEncoder instance
const textEncoder = /* @__PURE__ */ new TextEncoder();

/** Signs an AWS IoT Core websocket URL (SigV4) using the current Amplify session's credentials. */
export async function buildAwsWebsocketUrl(
	config: AwsIotCoreConfig,
): Promise<string> {
	const session = await fetchAuthSession();
	const credentials = session.credentials;
	if (!credentials) {
		throw new Error("No AWS credentials available");
	}

	const { accessKeyId, secretAccessKey, sessionToken } = credentials;
	const host = config.host;
	const region = config.region;
	const datetime = new Date().toISOString().replace(/[:-]|\.\d{3}/g, "");
	const date = datetime.slice(0, 8);

	const method = "GET";
	const protocol = "wss";
	const uri = "/mqtt";
	const service = "iotdevicegateway";
	const algorithm = "AWS4-HMAC-SHA256";
	const credentialScope = `${date}/${region}/${service}/aws4_request`;

	// Build the canonical query string for signing (excludes X-Amz-Security-Token)
	const canonicalQueryStringParts = [
		`X-Amz-Algorithm=${algorithm}`,
		`X-Amz-Credential=${encodeURIComponent(`${accessKeyId}/${credentialScope}`)}`,
		`X-Amz-Date=${datetime}`,
		`X-Amz-SignedHeaders=host`,
	];
	canonicalQueryStringParts.sort();
	const canonicalQueryString = canonicalQueryStringParts.join("&");

	const canonicalHeaders = `host:${host}\n`;
	const canonicalRequest = [
		method,
		uri,
		canonicalQueryString,
		canonicalHeaders,
		"host",
		EMPTY_PAYLOAD_SHA256,
	].join("\n");

	const stringToSign = [
		algorithm,
		datetime,
		credentialScope,
		await sha256Hex(textEncoder.encode(canonicalRequest)),
	].join("\n");

	// Derive signing key
	const kDate = await hmacSha256(
		textEncoder.encode(`AWS4${secretAccessKey}`),
		textEncoder.encode(date),
	);
	const kRegion = await hmacSha256(kDate, textEncoder.encode(region));
	const kService = await hmacSha256(kRegion, textEncoder.encode(service));
	const kSigning = await hmacSha256(
		kService,
		textEncoder.encode("aws4_request"),
	);

	const signature = bufToHex(
		await hmacSha256(kSigning, textEncoder.encode(stringToSign)),
	);

	// Build the final URL — append signature, then session token (unsigned)
	let finalUrl = `${protocol}://${host}${uri}?${canonicalQueryString}&X-Amz-Signature=${signature}`;
	if (sessionToken) {
		finalUrl += `&X-Amz-Security-Token=${encodeURIComponent(sessionToken)}`;
	}
	return finalUrl;
}

function toUint8Array(source: BufferSource): Uint8Array {
	// If it's already a view (TypedArray or DataView),
	// we use its internal buffer, offset, and length.
	if (ArrayBuffer.isView(source)) {
		return new Uint8Array(
			source.buffer,
			source.byteOffset,
			source.byteLength,
		);
	}
	// If it's a raw ArrayBuffer, wrap it directly.
	return new Uint8Array(source);
}

async function hmacSha256(
	key: BufferSource,
	data: BufferSource,
): Promise<ArrayBuffer> {
	const result = hmac(sha256, toUint8Array(key), toUint8Array(data));
	return result.buffer.slice(
		result.byteOffset,
		result.byteOffset + result.byteLength,
	) as ArrayBuffer;
}

async function sha256Hex(data: BufferSource): Promise<string> {
	const hash = sha256(toUint8Array(data));
	return bufToHex(hash.buffer);
}

function bufToHex(buf: ArrayBuffer): string {
	const bytes = new Uint8Array(buf);
	let hex = "";
	for (let i = 0; i < bytes.length; i++) {
		hex += HEX_CHARS[bytes[i]];
	}
	return hex;
}
